// The value grammar the sales routes' URL validators share (P3-E2 PR E2-1).
//
// Each helper reads one parameter DEFENSIVELY, the production and inventory
// house rule that `screens/search-params.ts` states in full: a value the list
// route would refuse 400 is dropped here, never thrown on, so a shared link
// with a stale parameter still opens the screen and no request carries it.
// Strictness still lives server-side (D-142).
//
// E2-1's two validator files (`screens/pipeline-search-params.ts` for the
// board, the deal list and the forecast, and `screens/tasks-search-params.ts`
// for the task list) read their parameters through these helpers. The three
// books' validators in `screens/search-params.ts` keep private copies of the
// same four rules, written before this file existed; they agree with these
// case for case, and `search-grammar.test.ts` pins every rule on its own.

import { VIEW_SEARCH_TEXT_MAX } from "../../views/lib/screens";

/** A uuid, loosely: the shape the platform mints (v7) without pinning the
 * version, which is what the list routes' `z.guid()` accepts. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** A calendar month as the contract's `SalesMonth` reads it: `YYYY-MM`, the
 * month 01 to 12 and the year 1900 to 2099. A year a `date` column cannot hold
 * answered 500 at four doors before the contract fenced it, so the fence is
 * restated here and a pasted `?close_from=0000-01` never becomes a request. */
const SALES_MONTH_RE = /^(19|20)\d{2}-(0[1-9]|1[0-2])$/u;

/** An id filter: a uuid, or absent. */
export function uuidParam(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

/** One of a closed set of tokens, or absent. */
export function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return typeof value === "string" && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/**
 * A flag the list route takes as `true` only (`rotting`, `needs_next_step`).
 * The router reads `?rotting=true` as a boolean; the quoted spelling a
 * hand-written link can carry is accepted too. `false` reads as absent,
 * because an absent flag already means "not only these", and a view stores
 * the flag only as `true` (spec 09 §4.14).
 */
export function trueOnlyParam(value: unknown): true | undefined {
  return value === true || value === "true" ? true : undefined;
}

/** Free text for a search box: 1 to 200 characters that are not all blank,
 * with no NUL character, which Postgres text cannot hold. A number the router
 * parsed out of a hand-typed `?q=123` is dropped rather than turned back into
 * text, because `?q=1e5` would come back as `100000`. */
export function searchTextParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (value.trim().length === 0 || value.length > VIEW_SEARCH_TEXT_MAX) return undefined;
  return value.includes("\u0000") ? undefined : value;
}

/** A calendar month, `YYYY-MM`, within the contract's 1900-01 to 2099-12. */
export function salesMonthParam(value: unknown): string | undefined {
  return typeof value === "string" && SALES_MONTH_RE.test(value) ? value : undefined;
}

/** Whole months from `from` to `to`, both `YYYY-MM` (negative when `to` is
 * earlier). Callers pass months `salesMonthParam` has already accepted. */
export function monthsBetween(from: string, to: string): number {
  const fromIndex = Number(from.slice(0, 4)) * 12 + Number(from.slice(5, 7));
  const toIndex = Number(to.slice(0, 4)) * 12 + Number(to.slice(5, 7));
  return toIndex - fromIndex;
}

/**
 * The both-ways pin between a screen's search type `S` and its saved-view
 * filter config `F` (the generated `SavedView…Config["filters"]`). A record
 * typed with it must name every config key and no other, and each member is
 * `true` only when `S` declares that key with a value the config accepts. A
 * filter the contract adds, drops or reshapes therefore fails tsc where the
 * record is written, and a Save as new built from the address can never send
 * a shape the server refuses.
 */
export type FilterKeyPin<S, F> = {
  readonly [K in keyof F]-?: K extends keyof S
    ? [NonNullable<S[K]>] extends [NonNullable<F[K]>]
      ? true
      : never
    : never;
};
